import {
  Button,
  Table,
  Row,
  Col,
  Spinner,
  Alert,
  Modal,
  Form,
} from "react-bootstrap";
import { useState } from "react";
import PageFade from "../../components/animation/PageFade";
import {
  useGetSizesQuery,
  useCreateSizeMutation,
  useUpdateSizeMutation,
  useDeleteSizeMutation,
} from "../../services/api";
import { toast } from "react-toastify";
import { Size } from "../../types";

export default function Sizes() {
  const { data, isLoading, error, refetch } = useGetSizesQuery();
  const [createSize, { isLoading: isCreating }] = useCreateSizeMutation();
  const [updateSize, { isLoading: isUpdating }] = useUpdateSizeMutation();
  const [deleteSize, { isLoading: isDeleting }] = useDeleteSizeMutation();

  const [showModal, setShowModal] = useState(false);
  const [editingSize, setEditingSize] = useState<Size | null>(null);
  const [formData, setFormData] = useState({ name: "" });

  const sizes = data?.data || [];

  const handleOpenModal = (size?: Size) => {
    if (size) {
      setEditingSize(size);
      setFormData({ name: size.name || "" });
    } else {
      setEditingSize(null);
      setFormData({ name: "" });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSize(null);
    setFormData({ name: "" });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Size name is required");
      return;
    }

    try {
      if (editingSize) {
        // According to guide: Use application/json for Sizes
        await updateSize({ id: editingSize._id, name: formData.name }).unwrap();
        toast.success("Size updated successfully");
      } else {
        // According to guide: Use application/json for Sizes
        await createSize({ name: formData.name }).unwrap();
        toast.success("Size created successfully");
      }
      handleCloseModal();
    } catch (error: any) {
      console.error('Size operation failed:', error);
      toast.error(
        error?.data?.message ||
          `Failed to ${editingSize ? "update" : "create"} size`
      );
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await deleteSize(id).unwrap();
        toast.success("Size deleted successfully");
      } catch (error: any) {
        toast.error(error?.data?.message || "Failed to delete size");
      }
    }
  };

  if (isLoading) {
    return (
      <PageFade>
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </PageFade>
    );
  }

  if (error) {
    const errorMessage =
      (error as any)?.data?.message ||
      (error as any)?.error ||
      "Failed to load sizes. Please check your connection and try again.";
    const status = (error as any)?.status;

    return (
      <PageFade>
        <Alert variant="danger">
          <Alert.Heading>Error Loading Sizes</Alert.Heading>
          <p>{errorMessage}</p>
          {status === 401 && (
            <p className="mb-0">
              <small>Your session may have expired. Please log in again.</small>
            </p>
          )}
          {status === 403 && (
            <p className="mb-0">
              <small>You don't have permission to access this resource.</small>
            </p>
          )}
          <hr />
          <div className="d-flex justify-content-end">
            <Button
              onClick={() => refetch()}
              variant="outline-danger"
              size="sm"
            >
              Retry
            </Button>
          </div>
        </Alert>
      </PageFade>
    );
  }

  return (
    <PageFade>
      <Row className="mb-3">
        <Col>
          <h3>Sizes</h3>
        </Col>
        <Col className="text-end">
          <Button onClick={() => handleOpenModal()}>Add Size</Button>
        </Col>
      </Row>
      {sizes.length === 0 ? (
        <Alert variant="info">
          No sizes found. Click "Add Size" to create your first size.
        </Alert>
      ) : (
        <Table hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sizes.map((size) => (
              <tr key={size._id} style={{ backgroundColor: "#fff" }}>
                <td>{size.name}</td>
                <td className="text-end">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="me-2"
                    onClick={() => handleOpenModal(size)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleDelete(size._id, size.name)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingSize ? "Edit Size" : "Add Size"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Size name (e.g., S, M, L, XL)"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating || isUpdating}>
              {isCreating || isUpdating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {editingSize ? "Updating..." : "Creating..."}
                </>
              ) : editingSize ? (
                "Update Size"
              ) : (
                "Create Size"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </PageFade>
  );
}
